import { Button, KBD, TypographyDetail } from '@seen/ui'
import { IconCheck, IconExternalLink, IconX } from '@tabler/icons-react'
import { type ReactNode, useCallback, useMemo } from 'react'
import { Link } from '#src/components/core/Link.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconInboxRead } from '#src/components/icons/IconInboxRead.tsx'
import { archiveInboxItemsVerified } from '#src/hooks/inbox/inboxLiveRemoval.ts'
import { useInboxArchiveAdvance } from '#src/hooks/inbox/useInboxArchiveAdvance.ts'
import { useInboxSiblings } from '#src/hooks/inbox/useInboxSiblings.ts'
import { useKnockFeedClient } from '#src/hooks/useKnockFeedClient.ts'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'

/* Shared sidebar header */
export function SidebarHeader({
  title,
  subtitle,
  actionUrl,
  actionLabel = 'Open chart',
  contextUrl,
  contextLabel = 'Open chart',
  notificationItem,
  onClose,
}: {
  title: string
  subtitle?: string
  actionUrl?: string
  actionLabel?: string
  contextUrl?: string
  contextLabel?: string
  notificationItem?: NotificationItem
  onClose: () => void
}): ReactNode {
  const feedClient = useKnockFeedClient()
  const getSiblings = useInboxSiblings()
  const advanceSelectionAwayFrom = useInboxArchiveAdvance()
  const showContextLink =
    !!contextUrl && (contextUrl !== actionUrl || contextLabel !== actionLabel)

  /* Resolve all siblings so toggle cascades to dedup-hidden notifications */
  const allItems = useMemo(() => {
    if (!notificationItem) {
      return []
    }
    const siblings = getSiblings(notificationItem)
    return siblings.length > 0 ? siblings : [notificationItem]
  }, [getSiblings, notificationItem])

  const isArchived = !!notificationItem?.archived_at
  const toggleDone = useCallback(() => {
    if (!notificationItem) {
      return
    }
    if (!isArchived) {
      const unarchived = allItems.filter((s) => !s.archived_at)
      if (unarchived.length > 0) {
        advanceSelectionAwayFrom(notificationItem.id)
        void archiveInboxItemsVerified(feedClient, unarchived)
      }
    } else {
      const archived = allItems.filter((s) => !!s.archived_at)
      if (archived.length > 0) {
        advanceSelectionAwayFrom(notificationItem.id)
        void feedClient.markAsUnarchived(archived)
      }
    }
  }, [
    feedClient,
    notificationItem,
    allItems,
    isArchived,
    advanceSelectionAwayFrom,
  ])

  const isRead = !!notificationItem?.read_at
  const toggleRead = useCallback(() => {
    if (!notificationItem) {
      return
    }
    if (isRead) {
      const read = allItems.filter((s) => !!s.read_at)
      if (read.length > 0) {
        void feedClient.markAsUnread(read)
      }
      onClose()
    } else {
      const unread = allItems.filter((s) => !s.read_at)
      if (unread.length > 0) {
        void feedClient.markAsRead(unread)
      }
    }
  }, [feedClient, notificationItem, allItems, isRead, onClose])

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-1.5">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium">{title}</div>
        {!!subtitle && (
          <div className="truncate text-xs text-muted-foreground">
            {subtitle}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!!actionUrl && (
          <Link
            to={actionUrl}
            className="inline-flex h-8 items-center gap-1 rounded-md px-3 text-sm font-medium whitespace-nowrap hover:bg-accent hover:text-accent-foreground"
          >
            <IconExternalLink className="size-4" />
            {actionLabel}
          </Link>
        )}
        {showContextLink && (
          <Link
            to={contextUrl}
            className="inline-flex h-8 items-center gap-1 rounded-md px-3 text-sm font-medium whitespace-nowrap hover:bg-accent hover:text-accent-foreground"
          >
            <IconExternalLink className="size-4" />
            {contextLabel}
          </Link>
        )}
        {!!notificationItem && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDone}
                aria-label={isArchived ? 'Mark as not done' : 'Mark as done'}
                data-dd-action-name="inbox.detail.toggle_done"
              >
                <IconCheck size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              className="flex items-center gap-2 px-2"
              side="bottom"
            >
              <TypographyDetail className="text-muted-foreground">
                {isArchived ? 'Mark as not done' : 'Mark as done'}
              </TypographyDetail>
              <KBD>E</KBD>
            </TooltipContent>
          </Tooltip>
        )}
        {!!notificationItem && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleRead}
                aria-label={isRead ? 'Mark as unread' : 'Mark as read'}
                data-dd-action-name="inbox.detail.toggle_read"
              >
                <IconInboxRead className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              className="flex items-center gap-2 px-2"
              side="bottom"
            >
              <TypographyDetail className="text-muted-foreground">
                {isRead ? 'Mark as unread' : 'Mark as read'}
              </TypographyDetail>
              <KBD>U</KBD>
            </TooltipContent>
          </Tooltip>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close detail panel"
          data-dd-action-name="inbox.detail.close"
        >
          <IconX className="h-5 w-5" />
        </Button>
      </div>
    </div>
  )
}
